/**
 * Offline write queue (domain §6): adding a transaction and approving/dismissing a capture work offline.
 * Requests are stored in localStorage and replayed in order when the connection returns. Transaction creates
 * carry a clientId, so a replay after a lost response is idempotent on the server.
 */
import { api, ApiError, NetworkError } from './api';
import { invalidateScopes } from './queries';

export interface Queued {
  id: string;
  method: string;
  path: string;
  body?: unknown;
  label: string;
  at: string;
}

const KEY = 'koverta.outbox';
const listeners = new Set<() => void>();
let snapshot: Queued[] = read();

function read(): Queued[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    return [];
  }
}
function write(q: Queued[]) {
  localStorage.setItem(KEY, JSON.stringify(q));
  snapshot = q;
  listeners.forEach((l) => l());
}

export const outbox = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  get: () => snapshot,
};

/** Send now; when the network is unavailable, queue it and resolve with { queued: true }. */
export async function sendOrQueue<T>(
  req: Omit<Queued, 'id' | 'at'>,
): Promise<{ queued: false; data: T } | { queued: true }> {
  if (navigator.onLine) {
    try {
      return { queued: false, data: await api<T>(req.path, { method: req.method, body: req.body }) };
    } catch (e) {
      if (!(e instanceof NetworkError)) throw e;
    }
  }
  write([...snapshot, { ...req, id: crypto.randomUUID(), at: new Date().toISOString() }]);
  return { queued: true };
}

let flushing = false;
export async function flushOutbox() {
  if (flushing || !snapshot.length || !navigator.onLine) return;
  flushing = true;
  try {
    while (snapshot.length) {
      const [next, ...rest] = snapshot;
      try {
        await api(next!.path, { method: next!.method, body: next!.body });
      } catch (e) {
        if (e instanceof NetworkError) break;
        // A permanent failure (e.g. the capture was already handled by the partner) is dropped.
        if (!(e instanceof ApiError)) break;
      }
      write(rest);
    }
  } finally {
    flushing = false;
    invalidateScopes(['transactions', 'plan', 'accounts', 'inbox', 'activity']);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flushOutbox());
}
